import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import { useEffect, type FC } from 'react';
import { useInView } from 'react-intersection-observer';
import { useDispatch } from 'react-redux';

import { isB2C } from '@/domains/core/settings/utils';
import { useCarouselPictures } from '@/domains/productDiscovery/ArticleReviews/ArticlePictures.ducks';
import { ArticleReviewsProvider } from '@/domains/productDiscovery/ArticleReviews/ArticleReviewsProvider';
import { MdScoresDynamic } from '@/domains/productDiscovery/ArticleReviews/modules/reviewsBlock/mdScores/MdScores.dynamic';
import { PicturesCarouselDynamic } from '@/domains/productDiscovery/ArticleReviews/modules/reviewsBlock/picturesCarousel/PicturesCarousel.dynamic';
import { Reviews } from '@/domains/productDiscovery/ArticleReviews/modules/reviewsBlock/reviews/Reviews';
import { ReviewsStats } from '@/domains/productDiscovery/ArticleReviews/modules/reviewsBlock/reviewsStats/ReviewsStats';
import { useReviewsStats } from '@/domains/productDiscovery/ArticleReviews/ReviewsStats.ducks';
import { GET_REVIEWS_STATS_TRIGGER } from '@/domains/productDiscovery/ArticleReviews/root.ducks.default';
import { trackScrollToUGC } from '@/domains/productDiscovery/ArticleReviews/utils.gtm';
import { useDetail } from '@/domains/productDiscovery/ProductPage/hooks/useDetail';

import styles from './reviewsBlock.module.scss';

const RankingModal = dynamic(
  () =>
    import(
      '@/domains/productDiscovery/ArticleReviews/modules/reviewsBlock/topRanked/RankingModal'
    ).then((mod) => mod.RankingModal),
  { ssr: false },
);

export const ReviewsBlock: FC = () => {
  const [ref, displayed] = useInView({ triggerOnce: true });

  const { asPath } = useRouter();
  const { articleId } = useDetail();
  const { pictures, fetched } = useCarouselPictures();
  const dispatch = useDispatch();

  const { fetched: reviewFetched, total } = useReviewsStats();

  useEffect(() => {
    if (displayed) {
      dispatch({ type: GET_REVIEWS_STATS_TRIGGER, articleId });
    }
  }, [displayed, dispatch, articleId]);

  useEffect(() => {
    if (fetched) {
      trackScrollToUGC(pictures);
    }
  }, [fetched, pictures]);

  if (reviewFetched && total === 0) return null;

  return (
    <ArticleReviewsProvider>
      <div
        ref={ref}
        data-testid="ReviewsContainer"
        className={styles.container}
      >
        {isB2C() && (
          <>
            <ReviewsStats />
            <MdScoresDynamic />
            <PicturesCarouselDynamic />
          </>
        )}
        <Reviews />
      </div>
      {asPath.includes('#ranking') && <RankingModal />}
    </ArticleReviewsProvider>
  );
};

ReviewsBlock.displayName = 'ReviewsBlock';
